import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "@/api/client";
import type { IdeaOut } from "@/api/types";
import { AuthorLabel } from "@/components/AuthorLabel";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";

type WorkLinks = {
  tasks: { id: string; title: string; url: string; completed: boolean }[];
  projects: { id: string; title: string; url: string; archived: boolean }[];
};

/**
 * The first thing on the page of an idea that was taken into work: what it
 * became and whether that is done. Without it the page reads like an idea that
 * is still waiting, which is exactly what confused people.
 */
export function ConvertedIdeaBanner({ idea }: { idea: IdeaOut }) {
  const query = useQuery({
    queryKey: ["idea-work", idea.id],
    queryFn: () => api<WorkLinks>(`/api/v1/ideas/${idea.id}/work-links`),
    enabled: idea.status === "converted",
  });
  if (idea.status !== "converted") return null;
  const tasks = query.data?.tasks ?? [];
  const projects = query.data?.projects ?? [];
  const method = idea.converted_to_method_id;
  const title = method && !tasks.length
    ? "Идея стала методом"
    : tasks.length > 1 || projects.length
      ? "Идея оформлена в работу"
      : "Идея стала задачей";
  return <Card className="space-y-2 border border-accent/40" role="status">
    <p className="text-cell font-medium text-text">✅ {title}</p>
    {tasks.map((t) => <Link key={t.id} to={t.url} className="flex min-h-touch items-center justify-between gap-2 break-words text-accent">
      <span>{t.title}</span>
      <span className="shrink-0 text-meta text-hint">{t.completed ? "выполнена ✓" : "в работе"}</span>
    </Link>)}
    {projects.map((p) => <Link key={p.id} to={p.url} className="flex min-h-touch items-center break-words text-accent">
      Проект: {p.title}{p.archived ? " · в архиве" : ""}
    </Link>)}
    {method && <Link to={`/knowledge/${method}`} className="flex min-h-touch items-center text-accent">Открыть метод</Link>}
    {query.isLoading && <p className="text-meta text-hint">Загружаем, во что превратилась идея…</p>}
  </Card>;
}

export function IdeaWorkLinks({ ideaId }: { ideaId: string }) {
  const query = useQuery({
    queryKey: ["idea-work", ideaId],
    queryFn: () => api<WorkLinks>(`/api/v1/ideas/${ideaId}/work-links`),
    refetchInterval: 15_000,
  });
  if (query.isLoading) return <p className="text-meta text-hint">Загружаем связанные задачи…</p>;
  if (query.isError) return <div role="alert">
    <p>Не удалось загрузить задачи и проекты идеи.</p>
    <Button variant="ghost" onClick={() => void query.refetch()}>Повторить</Button>
  </div>;
  if (!query.data || !query.data.tasks.length && !query.data.projects.length) return null;
  return <section className="space-y-1" aria-label="Задачи и проекты идеи">
    <h2 className="text-meta text-hint">Задачи и проекты идеи</h2>
    {query.data.projects.map((p) => <Link key={p.id} to={p.url} className="flex min-h-touch items-center break-words text-accent">
      Проект: {p.title}{p.archived ? " · в архиве" : ""}
    </Link>)}
    {query.data.tasks.map((t) => <Link key={t.id} to={t.url} className="flex min-h-touch items-center break-words text-accent">
      {t.completed ? "✓ " : "Задача: "}{t.title}
    </Link>)}
  </section>;
}

export function SourceIdeas({ kind, id }: { kind: "task" | "project"; id: string }) {
  const query = useQuery({
    queryKey: ["source-ideas", kind, id],
    queryFn: () => api<{ idea: IdeaOut; url: string }[]>(`/api/v1/${kind}s/${id}/source-ideas`),
    refetchInterval: 15_000,
  });
  if (query.isLoading) return <p className="text-meta text-hint">Загружаем исходные идеи…</p>;
  if (query.isError) return <Card role="alert" className="space-y-2">
    <p>Не удалось загрузить исходные идеи.</p>
    <Button variant="ghost" onClick={() => void query.refetch()}>Повторить</Button>
  </Card>;
  if (!query.data?.length) return null;
  return <Card className="space-y-3">
    <h2 className="text-cell-title font-medium">Исходные идеи и договорённости</h2>
    <p className="text-meta text-hint">Открой идею, чтобы прочитать общее обсуждение и посмотреть файлы.</p>
    {query.data.map(({ idea, url }) => <div key={idea.id} className="space-y-1">
      <Link to={url} className="flex min-h-touch items-center break-words text-accent">{idea.summary || idea.original_text.slice(0, 160)}</Link>
      <p><AuthorLabel authorId={idea.author_id} /></p>
    </div>)}
  </Card>;
}
